import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal, Typography, FloatingLabelInput, Button, Illustration } from "@aviary";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./JourneyWaitlistModal.styles";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (email: string) => void;
  prefilledEmail?: string;
  isLoading?: boolean;
  waitlistReason: "order_limit" | "zipcode" | null;
}

const JourneyWaitlistModal = ({
  isOpen,
  onClose,
  onSubmit,
  prefilledEmail,
  isLoading,
  waitlistReason,
}: Props) => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const [email, setEmail] = useState(prefilledEmail || "");

  const isMobile = tablet.lessThan;

  const handleSubmit = () => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) return;

    onSubmit?.(trimmedEmail);
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSubmit();
    }
  };

  useEffect(() => {
    if (prefilledEmail) {
      setEmail(prefilledEmail);
    }
  }, [prefilledEmail]);

  const title =
    waitlistReason === "order_limit"
      ? t(l.journeyCheckout.waitlist.titleOrderLimit)
      : t(l.journeyCheckout.waitlist.title);
  const description =
    waitlistReason === "order_limit"
      ? t(l.journeyCheckout.waitlist.descriptionOrderLimit)
      : t(l.journeyCheckout.waitlist.description);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="medium">
      <div css={styles.modalContainer}>
        <div css={styles.modalContent}>
          <div css={styles.iconSection}>
            <Illustration illustration="bell" />
          </div>

          <div css={styles.contentSection}>
            <div css={styles.textSection}>
              <Typography type="h4">{title}</Typography>
              <Typography type="body">{description}</Typography>
            </div>

            <div css={styles.formSection}>
              <FloatingLabelInput
                id="waitlist-email"
                label={t(l.journeyCheckout.waitlist.emailPlaceholder)}
                value={email}
                handleChange={e => setEmail(e.target.value)}
                onKeyDown={handleKeyDown}
                type="email"
                autoComplete="email"
              />

              <div css={styles.buttonContainer}>
                <Button
                  onClick={handleSubmit}
                  intention="primary"
                  disabled={!email.trim() || isLoading}
                  isLoading={isLoading}
                  isFullWidth={isMobile}
                  size={isMobile ? "large" : "medium"}
                >
                  {t(l.journeyCheckout.waitlist.submitButton)}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default JourneyWaitlistModal;
